import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUsers, FiPlus, FiTrash2, FiEdit2 } from 'react-icons/fi';
import TeamModal from '../components/TeamModal';

const Teams = () => {
  const { isAdmin } = useAuth();
  const { isDark } = useTheme();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await api.get('/teams');
      setTeams(response.data.teams || []);
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;

    try {
      await api.delete(`/teams/${id}`);
      setTeams(teams.filter(team => team.id !== id));
    } catch (error) {
      alert('Error deleting team');
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingTeam(null);
    fetchTeams();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDark ? 'border-gray-400' : 'border-white'}`}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pr-0 lg:pr-20">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1 lg:mb-2">Teams</h1>
          <p className="text-white/80 text-sm lg:text-base">Manage your teams and members</p>
        </div>
        {isAdmin && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowModal(true)}
            className="bg-white text-primary-600 px-5 py-3.5 rounded-xl font-semibold flex items-center justify-center space-x-2 active:bg-white/90 transition-all touch-manipulation shadow-lg w-full sm:w-auto"
          >
            <FiPlus size={20} />
            <span>Create Team</span>
          </motion.button>
        )}
      </div>

      {teams.length === 0 ? (
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-12 border border-white/20 text-center">
          <FiUsers className="mx-auto text-white/50 mb-4" size={48} />
          <p className="text-white/80 text-lg mb-2">No teams yet</p>
          <p className="text-white/60 text-sm">
            {isAdmin ? 'Create your first team to get started' : 'You are not part of any teams yet'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
          <AnimatePresence>
            {teams.map((team, index) => (
              <motion.div
                key={team.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <Link to={`/teams/${team.id}`}>
                      <h3 className="text-xl font-bold text-white mb-2 hover:text-primary-300 transition-colors">
                        {team.name}
                      </h3>
                    </Link>
                    {team.description && (
                      <p className="text-white/70 text-sm mb-3 line-clamp-2">
                        {team.description}
                      </p>
                    )}
                    <div className="flex items-center space-x-4 text-sm text-white/60">
                      <span className="flex items-center">
                        <FiUsers size={16} className="mr-1" />
                        {team.member_count || 0} members
                      </span>
                    </div>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex items-center space-x-2 pt-4 border-t border-white/10">
                    <button
                      onClick={() => handleEdit(team)}
                      className="flex-1 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center space-x-2"
                    >
                      <FiEdit2 size={16} />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(team.id)}
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-200 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <AnimatePresence>
        {showModal && (
          <TeamModal
            team={editingTeam}
            onClose={handleModalClose}
            onSuccess={fetchTeams}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Teams;
